import { formatMoney } from "../crm/crmHelpers";

/*
  Vocabulário e contas da aba Eventos.

  O estado da cobrança (A cobrar, Sem plano, Pago) vem do servidor como código;
  aqui só vira rótulo e cor. O mesmo vale para o nome do plano, o ciclo, o teto
  de parcelas e o texto de "como foi pago": o servidor devolve prontos, para
  esta tela não repetir regra do catálogo de planos.

  As respostas do onboarding (área e modo de trabalho) são códigos curtos
  gravados pela telemetria do cadastro, e o rótulo é o mesmo que a cliente vê.
*/

export const EVENT_STATE_META = {
  TO_CHARGE: { label: "A cobrar", tone: "warning" },
  NO_PLAN: { label: "Sem plano", tone: "neutral" },
  PAID: { label: "Pago", tone: "success" },
};

export const EVENT_STATE_ORDER = ["TO_CHARGE", "NO_PLAN", "PAID"];

/*
  Evento cadastrado. O status sai das datas, calculado no servidor (no fuso do
  Brasil); aqui só vira rótulo e cor.
*/
export const EVENT_STATUS_META = {
  ONGOING: { label: "Em andamento", tone: "success" },
  UPCOMING: { label: "Próximo", tone: "info" },
  ENDED: { label: "Encerrado", tone: "neutral" },
};

/** Endereço do balde "cadastros sem evento" (`/eventos/sem-evento`). */
export const NO_EVENT_ID = "sem-evento";

/** Como a cliente entrou no evento (o vínculo guarda o caminho). */
export const SOURCE_LABELS = {
  ONBOARDING: "pelo onboarding do evento",
  COUPON: "com o cupom do evento",
  MANUAL: "vinculada pela equipe",
};

/** "12/11/2026" ou "12 a 14/11/2026" a partir de AAAA-MM-DD (sem passar por Date). */
export const formatEventDates = (startsAt, endsAt) => {
  const parts = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
    return match ? { y: match[1], m: match[2], d: match[3] } : null;
  };

  const start = parts(startsAt);
  const end = parts(endsAt);
  if (!start) return "—";
  if (!end || (start.y === end.y && start.m === end.m && start.d === end.d)) {
    return `${start.d}/${start.m}/${start.y}`;
  }
  if (start.y === end.y && start.m === end.m) {
    return `${start.d} a ${end.d}/${end.m}/${end.y}`;
  }
  return `${start.d}/${start.m}/${start.y} a ${end.d}/${end.m}/${end.y}`;
};

/** O desconto do cupom como a equipe fala: "20%" ou "R$ 50,00". */
export const formatCouponDiscount = (coupon) => {
  if (!coupon) return "";
  const value = Number(coupon.discountValue) || 0;
  return coupon.discountType === "FIXED"
    ? formatMoney(value)
    : `${String(value).replace(".", ",")}%`;
};

/** Hoje (AAAA-MM-DD) no relógio de quem usa o painel, para o formulário. */
export const todayInput = (now = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const AREA_LABELS = {
  nail: "Unhas",
  lash: "Cílios",
  hair: "Cabelo",
  brow: "Sobrancelha",
  aesthetics: "Estética",
  barber: "Barbearia",
  other: "Outra área",
};

export const WORK_MODE_LABELS = {
  solo: "Sozinha",
  team: "Com equipe",
  rent: "Aluga espaço",
};

/** O rótulo conhecido, ou o próprio código se um dia aparecer um novo. */
export const labelOf = (labels, code) =>
  code ? labels[code] || String(code) : null;

/*
  As quatro formas de pagamento são as mesmas da comanda do app. Os códigos
  (CREDIT, DEBIT, PIX, CASH) são os que a aprovação do pedido aceita.
*/
export const PAYMENT_METHODS = [
  {
    value: "CREDIT",
    label: "Cartão de crédito",
    hint: "À vista ou parcelado",
  },
  { value: "DEBIT", label: "Cartão de débito", hint: "Sem parcelas" },
  { value: "PIX", label: "Pix", hint: "Transferência na hora" },
  { value: "CASH", label: "Dinheiro", hint: "Recebido em mãos" },
];

export const isCardMethod = (method) => method === "CREDIT" || method === "DEBIT";

/** Períodos do filtro de cadastros. "Tudo" não manda data nenhuma. */
export const PERIOD_OPTIONS = [
  { value: "all", label: "Todo o período" },
  { value: "today", label: "Hoje" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "30d", label: "Últimos 30 dias" },
];

/**
 * Início do período em instante ISO, a partir da meia-noite LOCAL de quem está
 * usando o painel (o evento acontece no fuso dele, não no do servidor).
 * `now` existe para o teste fixar o relógio.
 */
export const periodRange = (period, now = new Date()) => {
  const days = { today: 0, "7d": 6, "30d": 29 }[period];
  if (days === undefined) return {};

  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - days);

  return { from: start.toISOString() };
};

/** Parcela exibida na lista de parcelas: "6x de R$ 239,40". */
export const describeInstallment = (total, installments) => {
  const count = Math.max(Number(installments) || 1, 1);
  if (count === 1) return `À vista, ${formatMoney(total)}`;

  return `${count}x de ${formatMoney(Math.round((Number(total) * 100) / count) / 100)}`;
};

/**
 * Lê o valor digitado em reais. Aceita "1436,40", "1.436,40" e "1436.40"; o
 * servidor só entende ponto decimal, e mandar "1.436,40" cru viraria NaN.
 * Devolve `null` para o que não é um valor.
 */
export const parseMoneyInput = (text) => {
  const raw = String(text ?? "")
    .replace(/[^\d.,]/g, "")
    .trim();
  if (!raw) return null;

  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  const value = Number(normalized);
  return Number.isFinite(value) ? Math.round(value * 100) / 100 : null;
};

/** Valor para o campo de texto, no padrão brasileiro e sem o "R$". */
export const toMoneyInput = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/** "(11) 98765-4321" a partir de "+5511987654321"; o que não for BR segue cru. */
export const formatPhone = (phoneNumber) => {
  const digits = String(phoneNumber || "").replace(/\D/g, "");
  if (!digits) return null;

  const national =
    digits.length >= 12 && digits.startsWith("55") ? digits.slice(2) : digits;

  if (national.length === 11) {
    return `(${national.slice(0, 2)}) ${national.slice(2, 7)}-${national.slice(7)}`;
  }
  if (national.length === 10) {
    return `(${national.slice(0, 2)}) ${national.slice(2, 6)}-${national.slice(6)}`;
  }
  return String(phoneNumber).trim();
};

/**
 * Há quanto tempo a pessoa se cadastrou. Num evento a diferença que importa é
 * de minutos, e "hoje" não diz isso. `now` existe para o teste.
 */
export const formatSince = (value, now = new Date()) => {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return "—";

  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000);
  if (minutes < 1) return "agora";
  if (minutes < 60) return `há ${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;

  const days = Math.floor(hours / 24);
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
};

/*
  Cobrança na maquininha (Mercado Pago Point). Os estados vêm do servidor; aqui só
  viram texto e cor. O texto diz o que a EQUIPE faz a seguir, não o nome técnico
  do estado.
*/
export const POINT_ACTIVE_STATUSES = [
  "CREATING",
  "CREATED",
  "AT_TERMINAL",
  "ACTION_REQUIRED",
];

export const POINT_STATUS_META = {
  CREATING: { label: "Enviando para a maquininha", tone: "info" },
  CREATED: { label: "Aguardando a cliente passar o cartão", tone: "warning" },
  AT_TERMINAL: { label: "Cartão sendo passado na maquininha", tone: "warning" },
  ACTION_REQUIRED: { label: "Confira o resultado na maquininha", tone: "warning" },
  APPROVED: { label: "Pagamento aprovado", tone: "success" },
  REJECTED: { label: "Cartão recusado", tone: "danger" },
  CANCELED: { label: "Cobrança cancelada", tone: "neutral" },
  EXPIRED: { label: "A cobrança expirou na maquininha", tone: "neutral" },
  FAILED: { label: "O Mercado Pago não aceitou a cobrança", tone: "danger" },
  REVIEW: { label: "Pagamento em conferência", tone: "danger" },
  REFUNDED: { label: "Pagamento estornado", tone: "neutral" },
};

export const isPointChargeActive = (charge) =>
  Boolean(charge) && POINT_ACTIVE_STATUSES.includes(charge.status);

/** Aprovada no Mercado Pago, mas o plano ainda não foi liberado. */
export const isPointChargeSettling = (charge) =>
  Boolean(charge) && charge.status === "APPROVED" && !charge.used;

/** O servidor ainda vai mudar este estado sozinho: vale continuar consultando. */
export const shouldPollPointCharge = (charge) =>
  isPointChargeActive(charge) || isPointChargeSettling(charge);

/** Pagamento aprovado E plano liberado: a cobrança terminou bem. */
export const isPointChargeDone = (charge) =>
  Boolean(charge) && charge.status === "APPROVED" && Boolean(charge.used);

/** Termina sem dinheiro recebido: dá para cobrar de novo. */
export const canChargeAgain = (charge) =>
  Boolean(charge) &&
  ["REJECTED", "CANCELED", "EXPIRED", "FAILED"].includes(charge.status);

/** Por que a cobrança direto na maquininha não está disponível. */
export const POINT_UNAVAILABLE_TEXT = {
  NOT_CONFIGURED:
    "A cobrança direto na maquininha ainda não está ligada neste ambiente.",
  WALLET_NOT_CONNECTED:
    "A conta do Mercado Pago da Relinq ainda não está conectada.",
  NO_TERMINALS:
    "Nenhuma maquininha cadastrada para a Relinq. Cadastre em Pagamentos > Maquininhas.",
};

/** O que falta ligar, quando a tela de cadastro das maquininhas não pode abrir. */
export const POINT_SETUP_TEXT = {
  NOT_CONFIGURED:
    "Falta definir no servidor qual empresa recebe os planos (variável PLATFORM_POINT_COMPANY_ID). Sem isso a cobrança na maquininha fica desligada.",
  WALLET_NOT_CONNECTED:
    "A conta do Mercado Pago da Relinq ainda não está conectada. Conecte em Carteira, no app, entrando como a empresa da Relinq.",
};

/** "Cartão de crédito em 6x", "Cartão de débito"... do que a máquina informou. */
export const describePointPayment = (charge) => {
  if (!charge) return null;
  if (charge.paymentId === "CARTAO_DEBITO") return "Cartão de débito";
  if (charge.paymentId === "CARTAO_CREDITO") {
    return charge.installments > 1
      ? `Cartão de crédito em ${charge.installments}x`
      : "Cartão de crédito à vista";
  }
  return "Cartão";
};

/**
 * Situação do cupom como a equipe lê: pausado, ainda não começou, expirado,
 * esgotado ou ativo. O servidor guarda só `isActive`, datas e contagem; a
 * conclusão sai daqui (igual à tabela de Cupons).
 */
export const couponStatus = (coupon, today = todayInput()) => {
  if (!coupon) return null;
  if (!coupon.isActive) return { label: "Pausado", tone: "neutral" };

  const starts = coupon.startsAt ? String(coupon.startsAt).slice(0, 10) : null;
  const expires = coupon.expiresAt ? String(coupon.expiresAt).slice(0, 10) : null;

  if (expires && expires < today) return { label: "Expirado", tone: "warning" };
  if (starts && starts > today) return { label: "Ainda não começou", tone: "info" };
  if (coupon.usageLimit != null && coupon.usageCount >= coupon.usageLimit) {
    return { label: "Esgotado", tone: "warning" };
  }
  return { label: "Ativo", tone: "success" };
};
